// pages/TaskManagement/TasksIveAssigned.js
//
// Legacy menu: "View Tasks I've Assigned"
// Round TM-10 (2026) — legacy flag semantics (audit Deliverable 10):
//   Open     = OriginatorMarkComplete=0, Rejected=0, not Meeting
//   Rejected = the rejected-assignment rows (reassign from the task page)
//   Closed   = OriginatorMarkComplete=1
// Rows exclude self-assignments; totals are the assignee's own
// user-stated labor.
// Backend: GET /api/TaskManagement/assigned-by-me?tab=

import React, { useCallback, useEffect, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader, EmptyState, ResponsiveTabs, TableSkeleton
} from "../../Components/Common/DashboardKit";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import api from "../../api";

const TABS = [
    { key: "open", label: "Open" },
    { key: "rejected", label: "Rejected" },
    { key: "closed", label: "Closed" },
];

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");
const fmtMins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;

const TasksIveAssigned = () => {
    document.title = "Tasks I've Assigned || SSW Technologies Dashboard";
    const navigate = useNavigate();

    const [tab, setTab] = useState("open");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchRows = useCallback(async (tabKey) => {
        try {
            setLoading(true);
            setError("");
            const res = await api.get("/TaskManagement/assigned-by-me", {
                params: { tab: tabKey },
            });
            const data = res?.data ?? res;
            setRows(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
        } catch (err) {
            setRows([]);
            setError(err?.response?.data?.message || "Unable to load tasks.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRows(tab); }, [tab, fetchRows]);

    const exportCsv = () => exportRowsToCsv(
        `tasks-ive-assigned-${tab}`,
        rows.map((r) => ({
            TaskID: r.taskId,
            DueDate: fmtDate(r.dueDate),
            AssignedTo: r.assignToName,
            Description: r.description,
            CreatedOn: fmtDate(r.createdOn),
            Activities: r.activitiesLogged,
            TotalMinutes: r.totalTimeMinutes,
        }))
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-send-plane-line"
                    title="Tasks I've Assigned"
                    subtitle="Everything you created, by assignee"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Tasks I've Assigned" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <ResponsiveTabs tabs={TABS} activeKey={tab} onSelect={(k) => setTab(k)} />
                            <div className="d-flex align-items-center gap-2">
                                <Badge color="light" className="text-muted">{rows.length}</Badge>
                                <Button size="sm" color="light" onClick={exportCsv} disabled={rows.length === 0}>
                                    <i className="ri-download-2-line align-bottom me-1"></i>CSV
                                </Button>
                                <Button size="sm" color="light" onClick={() => fetchRows(tab)}>
                                    <i className="ri-refresh-line align-bottom"></i>
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton rows={8} cols={6} />
                        ) : rows.length === 0 ? (
                            <EmptyState
                                icon="ri-inbox-line"
                                title="No tasks"
                                message="Nothing in this tab. Rejected tasks can be reassigned from the task page."
                            />
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Due</th>
                                            <th>Assigned To</th>
                                            <th>Description</th>
                                            <th>Created</th>
                                            <th className="text-end">Activities</th>
                                            <th className="text-end">Total Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr
                                                key={`${r.taskId}-${r.assignTo}-${i}`}
                                                role="button"
                                                onClick={() => navigate(`/task-management/view-task/${r.taskId}`)}
                                                title={`Open Task #${r.taskId}`}
                                            >
                                                <td className="fw-semibold">{fmtDate(r.dueDate)}</td>
                                                <td>
                                                    {r.assignToName}
                                                    {tab === "rejected" && (
                                                        <Badge color="danger" className="ms-2">Rejected</Badge>
                                                    )}
                                                </td>
                                                <td className="text-truncate" style={{ maxWidth: "420px" }}>{r.description}</td>
                                                <td>{fmtDate(r.createdOn)}</td>
                                                <td className="text-end">{r.activitiesLogged}</td>
                                                <td className="text-end">{fmtMins(r.totalTimeMinutes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TasksIveAssigned;
